import { lazy, Suspense, useRef, useState } from 'react';
import { useQc } from './context';
import { useCustomPresets } from './customPresetsContext';
import { presetExists } from './customPresets';
import { DEFAULT_PRESET } from '../../core';
import { PRESET_NAMES, presetOptionLabel } from './presetLabels';
import { useLicense } from '../license/context';
import { ProLock } from '../license/UpgradePrompt';
import './QcControls.css';

// The authoring dialog is a lazy chunk (spec §8): none of the editor UI (or its
// `customPresetDraft` defaults) reaches the eager, budget-measured bundle. It is
// mounted only while open (StudioPanel pattern).
const CustomPresetEditor = lazy(() =>
  import('./CustomPresetEditor').then((m) => ({ default: m.CustomPresetEditor })),
);

/**
 * The QC preset selector + Run control (cue-forge-khy). Extracted from the QC
 * route so it can live in the Studio toolbar as well: QC is not a separate mode
 * but a tool that acts on the loaded document, and its results already surface in
 * the shared Inspector. Reads/writes the QC context, so wherever it is mounted it
 * drives the same run.
 *
 * The picker gains a "Custom" optgroup of the operator's saved presets and a
 * ProLock "Customize…" button that opens the lazy authoring dialog
 * (cue-forge-3r9). Running/selecting any preset is free; only authoring is Pro.
 */
export function QcControls() {
  const { preset, setPreset, run } = useQc();
  const { presets: customs } = useCustomPresets();
  const { isPro } = useLicense();
  const [editorOpen, setEditorOpen] = useState(false);
  const customizeRef = useRef<HTMLButtonElement>(null);
  const [announce, setAnnounce] = useState('');

  // Explicit-run guard (spec §6): a selected preset that no longer resolves (a
  // custom preset deleted here or in another tab) must block and be surfaced, never
  // silently substitute a green-looking Generic result in a compliance product.
  function onRun() {
    if (!presetExists(preset, customs)) {
      setPreset(DEFAULT_PRESET);
      setAnnounce(
        'The selected preset no longer exists — switched to Generic. Re-run to continue.',
      );
      return;
    }
    setAnnounce('');
    run();
  }

  function closeEditor() {
    setEditorOpen(false);
    customizeRef.current?.focus();
  }

  return (
    <div className="cf-qc-controls">
      <label className="cf-field">
        <span>Preset</span>
        <select
          aria-label="QC preset"
          className="cf-select"
          value={preset}
          onChange={(e) => setPreset(e.target.value)}
        >
          {PRESET_NAMES.map((name) => (
            <option key={name} value={name}>
              {presetOptionLabel(name)}
            </option>
          ))}
          {customs.length > 0 && (
            <optgroup label="Custom">
              {customs.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.ruleset.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </label>
      <button type="button" className="cf-btn cf-btn--primary" onClick={onRun}>
        Run QC
      </button>
      <button
        ref={customizeRef}
        type="button"
        className="cf-btn"
        onClick={() => setEditorOpen(true)}
      >
        Customize{'…'}
        {!isPro && (
          <>
            {' '}
            <ProLock />
          </>
        )}
      </button>
      {/* Always-mounted polite live region for the explicit-run GUARD only (it
          must exist before the message is set or it is never announced). The run
          summary itself is composed by QcProvider into the Inspector's single
          announcer, together with the primed cursor position — two regions
          updating in one action would stack SR utterances (cue-forge-drt.18) —
          and only on explicit runs, so an edit-driven background re-run still
          never re-announces (cue-forge-zxw.9). The guard early-returns without
          calling run(), so this region never fires in the same action as the
          Inspector's announcer. */}
      <span className="cf-qc-controls__status" role="status" aria-live="polite">
        {announce}
      </span>
      {editorOpen && (
        <Suspense fallback={null}>
          <CustomPresetEditor onClose={closeEditor} />
        </Suspense>
      )}
    </div>
  );
}
